import type { ShowcaseBeacon, ShowcasePublicVideo } from "@shared/showcase";

/**
 * The watch page's three calls. Same origin: the edge Worker forwards
 * `/api/*` on video.nenda.com to the Foyer API.
 */
const BASE = "/api/showcase/v1/public";

export class NotFoundError extends Error {}

export async function fetchVideo(slug: string): Promise<ShowcasePublicVideo> {
  const res = await fetch(`${BASE}/videos/${encodeURIComponent(slug)}`);
  if (res.status === 404) throw new NotFoundError("not_found");
  if (!res.ok) throw new Error(`http_${res.status}`);
  return (await res.json()) as ShowcasePublicVideo;
}

/** Null when analytics are unavailable: playback never depends on them. */
export async function startSession(
  slug: string,
  ref: string | null
): Promise<string | null> {
  try {
    const res = await fetch(
      `${BASE}/videos/${encodeURIComponent(slug)}/sessions`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(ref ? { ref } : {}),
      }
    );
    if (!res.ok) return null;
    return ((await res.json()) as { sessionId?: string }).sessionId ?? null;
  } catch {
    return null;
  }
}

/**
 * `sendBeacon` survives the tab closing, which is exactly when the last
 * stretch of a session would otherwise be lost. Falls back to a keepalive
 * fetch where sendBeacon is missing or refuses.
 */
export function sendBeacon(beacon: ShowcaseBeacon): void {
  const body = JSON.stringify(beacon);
  const url = `${BASE}/beacon`;
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return;
  } catch {
    // fall through
  }
  void fetch(url, {
    method: "POST",
    headers: { "content-type": "text/plain" },
    body,
    keepalive: true,
  }).catch(() => {});
}
